import Contrat from '@/generated/classes/Contrat';
import {
  ControleSignatureMasseQuery,
  type ControleSignatureMasseManque,
} from '@/extend/classes/ResultatContratMasse';
import { type ControleSignatureMasseManqueCodes } from '@/generated/classes/ResultatContratMasse';

// Logique de l'envoi en signature électronique en masse, isolée de l'UI (OVA-4753 / OVA-4979).
//
// Avant tout envoi, la sélection traverse une suite d'étapes de contrôle, dans l'ordre fixé par l'US :
// la société employeuse, puis les salariés, puis les contrats eux-mêmes. Chaque étape peut proposer à
// l'utilisateur de compléter ce qui manque — c'est le cas de la société, dont l'information est saisie
// une fois pour tous les contrats — ou se contenter d'écarter les contrats incomplets.
//
// Ce module ne porte que l'enchaînement. Les étapes sont fournies par l'appelant : en ajouter une ne
// demande pas de retoucher les autres ni `executerControles`.
//
// Le parcours unitaire fait ces contrôles côté serveur, contrat par contrat, au fil des appels
// (`ContratManager.CheckContrat`, `ContratManager.Submit`). Les rejouer tels quels en masse ouvrirait
// les modales au milieu du lot : on les fait donc une fois, en amont, avant tout appel sur les contrats.

/**
 * Contrôle serveur (`controleSignatureMasse`) partagé par toutes les étapes d'un parcours.
 *
 * Le serveur contrôle chaque contrat indépendamment des autres : un seul contrôle de la sélection, filtré
 * ensuite sur les contrats que voit chaque étape, vaut un contrôle par étape. Il n'est rechargé qu'après
 * une complétion (`invalider`, appelé par `executerControles`), au lieu d'un appel par étape — chacun
 * pouvant rendre des documents côté serveur (revue des PR de la signature en lot, 15/10/2026).
 */
export interface SourceControle {
  /**
   * Manques des contrats fournis, restreints à eux. Le contrôle n'est rappelé que s'il a été invalidé ou
   * s'il ne couvre pas tous ces contrats. Une erreur est propagée : un contrôle illisible ne prouve rien.
   */
  manques(contrats: Contrat[]): Promise<ControleSignatureMasseManque[]>;
  /** Oublie le dernier contrôle : les données ont pu changer. */
  invalider(): void;
}

export function creerSourceControle(): SourceControle {
  let manquesCharges: ControleSignatureMasseManque[] = [];
  let couverts: Set<number> | null = null;

  return {
    async manques(contrats: Contrat[]): Promise<ControleSignatureMasseManque[]> {
      const ids = contrats.map((contrat) => contrat.id);
      if (couverts == null || ids.some((id) => !couverts!.has(id))) {
        const query = new ControleSignatureMasseQuery();
        query.contratIds = ids;
        const controle = await Contrat.controleSignatureMasse(query);
        manquesCharges = controle.manques ?? [];
        couverts = new Set(ids);
      }
      const demandes = new Set(ids);
      return manquesCharges
        .map((manque) => {
          const copie = { ...manque } as ControleSignatureMasseManque;
          copie.contratIds = manque.contratIds.filter((id) => demandes.has(id));
          return copie;
        })
        .filter((manque) => manque.contratIds.length > 0);
    },

    invalider(): void {
      couverts = null;
    },
  };
}

/** Étapes du parcours, dans l'ordre de l'US. */
export type CodeEtape = 'SOCIETE' | 'SALARIE' | 'CONTRAT';

/** Information obligatoire absente, et contrats qu'elle bloque. */
export interface Manque {
  /** Code stable, propre à l'étape (ex. `IDENTITE_TVA`). */
  code: ControleSignatureMasseManqueCodes;
  /** Libellé affichable, repris dans les messages à l'utilisateur. */
  libelle: string;
  /** Contrats bloqués par ce manque. */
  contratIds: number[];
}

/** Raison pour laquelle un contrat ne partira pas en signature. */
export interface Exclusion {
  /** Première étape ayant écarté le contrat ; les suivantes ne le voient plus. */
  etape: CodeEtape;
  /** Manques de cette étape qui le concernent. */
  manques: Manque[];
}

/**
 * Une étape de contrôle.
 *
 * `charger` et `controler` sont séparés pour que le pipeline puisse relire les données après une
 * complétion, puis contrôler de nouveau, sans que l'étape ait à orchestrer elle-même cette reprise.
 */
export interface EtapeControle {
  readonly code: CodeEtape;
  /**
   * (Re)lit depuis le serveur les données dont l'étape a besoin. Appelée avant le premier contrôle,
   * puis après chaque complétion. Une erreur est propagée : un contrôle fait sur des données
   * illisibles ne prouverait rien.
   */
  charger?(contrats: Contrat[]): Promise<void>;
  /** Liste ce qui manque, à partir des données chargées. Tableau vide : étape franchie. */
  controler(contrats: Contrat[]): Manque[];
  /**
   * Propose à l'utilisateur de compléter. Résout `false` s'il abandonne d'emblée : on ne relit alors
   * rien. Résout `true` sinon, ce qui ne garantit pas que tout ait été saisi — le pipeline relit et
   * contrôle de nouveau pour le savoir.
   */
  completer?(contrats: Contrat[], manques: Manque[]): Promise<boolean>;
}

/** Issue de l'ensemble des contrôles. */
export interface IssueControles {
  /** Contrats ayant franchi toutes les étapes, dans l'ordre de la sélection. */
  eligibles: Contrat[];
  /** Contrats écartés, indexés par id. */
  exclusions: Map<number, Exclusion>;
}

/**
 * Fait traverser à `contrats` les étapes fournies, dans l'ordre.
 *
 * Pour chaque étape : chargement, contrôle, puis, s'il manque quelque chose et que l'étape sait le
 * faire compléter, complétion, nouveau chargement et nouveau contrôle — c'est la « reprise
 * automatique de l'analyse » de l'US. Une seule complétion par étape : si l'utilisateur referme la
 * modale sans tout saisir, on ne la lui rouvre pas en boucle.
 *
 * Les contrats encore bloqués à l'issue d'une étape sont écartés, et les étapes suivantes ne
 * portent que sur les autres : un contrat n'est jamais compté deux fois, et les contrats
 * éligibles poursuivent même si d'autres sont bloqués (règle de l'US). Quand plus aucun contrat ne
 * reste, les étapes suivantes ne sont pas jouées.
 *
 * `source` : contrôle serveur partagé par les étapes, invalidé ici après chaque complétion.
 */
export async function executerControles(
  contrats: Contrat[],
  etapes: EtapeControle[],
  source?: SourceControle,
): Promise<IssueControles> {
  const exclusions = new Map<number, Exclusion>();
  let restants = contrats;

  for (const etape of etapes) {
    if (restants.length == 0) break;

    await etape.charger?.(restants);
    let manques = etape.controler(restants);

    if (manques.length > 0 && etape.completer) {
      if (await etape.completer(restants, manques)) {
        // La complétion a modifié des données : le contrôle partagé est périmé, pour cette étape comme
        // pour les suivantes.
        source?.invalider();
        await etape.charger?.(restants);
        manques = etape.controler(restants);
      }
    }

    manques.forEach((manque) => {
      manque.contratIds.forEach((contratId) => {
        const exclusion = exclusions.get(contratId);
        if (exclusion == undefined) {
          exclusions.set(contratId, { etape: etape.code, manques: [manque] });
        } else if (exclusion.etape == etape.code) {
          exclusion.manques.push(manque);
        }
      });
    });
    restants = restants.filter((contrat) => !exclusions.has(contrat.id));
  }

  return { eligibles: restants, exclusions };
}
